import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";
import { createPortal } from "react-dom";
import { dashFetch } from "@/api/client";
import { getDashSocket } from "@/socket/dashSocket";
import { DASH_SOCKET_EVENTS } from "@/socket/events";

export type DeployProgressEvent = {
  jobId?: string;
  domain?: string;
  type?: string;
  path?: string;
  status?: string;
  percent?: number;
  label?: string;
  message?: string;
  processed?: number;
  total?: number;
  error?: string;
};

type DeployProgressContextValue = {
  activeJob: DeployProgressEvent | null;
  trackDeploy: (jobId: string, seed?: Partial<DeployProgressEvent>) => void;
  dismissOverlay: () => void;
};

const DeployProgressContext = createContext<DeployProgressContextValue | null>(null);

export function useDeployProgress(): DeployProgressContextValue {
  const ctx = useContext(DeployProgressContext);
  if (!ctx) throw new Error("useDeployProgress must be used within DeployProgressProvider");
  return ctx;
}

export function useOptionalDeployProgress(): DeployProgressContextValue | null {
  return useContext(DeployProgressContext);
}

function DeployProgressOverlay({
  job,
  onDismiss
}: {
  job: DeployProgressEvent;
  onDismiss: () => void;
}) {
  const pct = Math.max(0, Math.min(100, Number(job.percent || 0) || 0));
  const done = job.status === "done";
  const failed = job.status === "error";
  const title = job.type === "site" || job.type === "all" ? "Site deployment" : "Page deployment";
  const label =
    failed
      ? `Failed: ${String(job.error || job.message || "unknown")}`
      : done
        ? String(job.message || job.label || "Deployment complete")
        : String(job.label || job.message || "Working…");
  const meta =
    job.type === "site" || job.type === "all"
      ? job.total
        ? `${String(job.processed || 0)} / ${String(job.total)}${job.path ? ` · ${job.path}` : ""}`
        : job.path || ""
      : job.path || "";

  return createPortal(
    <div id="deployOverlay" className="deployOverlay on" aria-hidden="false">
      <div className="deployBackdrop" onClick={onDismiss} />
      <div className="deployCard" role="dialog" aria-modal="true" aria-label={title}>
        <div className="deployTitle">{title}</div>
        <div className="deployLabel">{label}</div>
        <div className="deployBar">
          <div className="deployBarFill" style={{ width: `${done ? 100 : failed ? pct : pct}%` }} />
        </div>
        {meta ? <div className="deployMeta mono">{meta}</div> : null}
        <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="btn" onClick={onDismiss}>
            {done || failed ? "Close" : "Run in background"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function DeployProgressProvider({ children }: { children: ReactNode }) {
  const [activeJob, setActiveJob] = useState<DeployProgressEvent | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const trackedJobIdRef = useRef("");
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const ingest = useCallback((ev: DeployProgressEvent) => {
    const jid = String(ev?.jobId || "");
    if (!jid || jid !== trackedJobIdRef.current) return;
    setActiveJob((prev) => ({ ...(prev || {}), ...ev, jobId: jid }));
    if (ev.status === "done" || ev.status === "error") {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }
  }, []);

  const trackDeploy = useCallback((jobId: string, seed?: Partial<DeployProgressEvent>) => {
    const jid = String(jobId || "").trim();
    if (!jid) return;
    trackedJobIdRef.current = jid;
    setDismissed(false);
    setActiveJob({
      jobId: jid,
      status: "running",
      percent: 2,
      label: "Starting deployment…",
      ...seed
    });

    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      void (async () => {
        if (trackedJobIdRef.current !== jid) return;
        try {
          const data = await dashFetch<{ ok?: boolean; job?: DeployProgressEvent }>(
            `/dashboard/api/deploy/status?jobId=${encodeURIComponent(jid)}`,
            { cache: "no-store" }
          );
          const job = data?.job;
          if (job) ingest({ ...job, jobId: jid });
        } catch {
          /* ignore poll errors */
        }
      })();
    }, 2000);
  }, [ingest]);

  const dismissOverlay = useCallback(() => {
    setDismissed(true);
    trackedJobIdRef.current = "";
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setActiveJob(null);
  }, []);

  useEffect(() => {
    const s = getDashSocket();
    if (!s) return;
    const onProgress = (ev: DeployProgressEvent) => ingest(ev);
    s.on(DASH_SOCKET_EVENTS.deployProgress, onProgress);
    return () => {
      s.off(DASH_SOCKET_EVENTS.deployProgress, onProgress);
    };
  }, [ingest]);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const value = useMemo(
    () => ({ activeJob, trackDeploy, dismissOverlay }),
    [activeJob, trackDeploy, dismissOverlay]
  );

  const showOverlay = Boolean(activeJob?.jobId && !dismissed);

  return (
    <DeployProgressContext.Provider value={value}>
      {children}
      {showOverlay && activeJob ? (
        <DeployProgressOverlay job={activeJob} onDismiss={dismissOverlay} />
      ) : null}
    </DeployProgressContext.Provider>
  );
}
